import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addAddress, editAddress, deleteAddress, setDefaultAddress, fetchAddresses } from '../../store';
import './Addresses.css';

const Addresses = () => {
    const dispatch = useDispatch();
    const addresses = useSelector(state => state.addresses.items);
    const addressesFetched = useSelector(state => state.addresses.fetched) || false; // Check if addresses are fetched
    const userId = useSelector(state => state.user.userId); // Fetch userId from store,

    useEffect(() => {
        if (userId && !addressesFetched) {
            dispatch(fetchAddresses(userId)); // Fetch addresses when component mounts
        }
    }, [dispatch, userId, addressesFetched]);
    const [editingId, setEditingId] = useState(null);
    const [formState, setFormState] = useState({});
    const [isDeleting, setIsDeleting] = useState(false);
    const [, setIsNewAddress] = useState(false); // Flag to distinguish mode

    const handleEditClick = (address) => {
        if (editingId === address.id) {
            // Save
            if (!validateForm()) return;
            dispatch(editAddress({ userId: userId, address: formState }));
            setEditingId(null);
            setIsNewAddress(false);
        } else {
            setEditingId(address.id);
            setIsNewAddress(false);
            setFormState({ ...address });
        }
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => ({ ...prev, [name]: value }));
    };

    const handleAddAddress = () => {
        setEditingId('new');
        setIsNewAddress(true);
        setFormState({
            addressName: "", // Default value
            fullName: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            pincode: '',
            country: '',
            phone: ''
        });

    };


    const handleDeleteAddress = async (addressId) => {
        setIsDeleting(true);
        dispatch(deleteAddress({ userId, addressId }));
        setIsDeleting(false);
    };


    const handleSetDefault = (addressId) => {
        dispatch(setDefaultAddress({ userId, addressId })).then(() => {
            dispatch(fetchAddresses(userId)); // Refresh addresses after setting default
        });
    };

    const getFieldTooltip = (field) => {
        const tooltips = {
            addressName: "Label this address (e.g. Home, Work)",
            fullName: "Recipient name",
            addressLine1: "Street address",
            addressLine2: "Apartment, suite, etc. (optional)",
            city: "City or town",
            state: "State or province",
            pincode: "ZIP or postal code",
            country: "Country",
            phone: "Phone number (with country code)"
        };
        return tooltips[field] || "";
    };


    const validateForm = () => {
        const { fullName, addressLine1, city, state, pincode, country, phone } = formState;
        return !!(fullName && addressLine1 && city && state && pincode && country && phone);
    };

    const renderAddressCard = (addr, isEditing = false, isNew = false) => (
        <div className={`address-card${addr.isDefault ? ' default' : ''}`} key={addr.id || 'new'}>
            <div className="address-type">
                {isEditing ? (
                    <input
                        className="form-input"
                        name="addressName"
                        placeholder="Label like Home / Work"
                        value={formState.addressName || ''}
                        onChange={handleInputChange}
                        style={{
                            fontWeight: '600',
                            marginBottom: '8px',
                            width: '100%'
                        }}
                    />
                ) : (
                    <span className="address-type-label">{addr.addressName || 'Home'}</span>
                )}
                {!isNew && addr.isDefault && <span className="default-badge">Default</span>}
            </div>

            <div className="address-details">
                {["fullName", "addressLine1", "addressLine2", "city", "state", "pincode", "country", "phone"]
                    .map(field => (
                        <div key={field}>
                            {isEditing ? (
                                <div className="input-tooltip-wrapper" style={{ position: 'relative' }}>
                                    <input
                                        className="form-input"
                                        name={field}
                                        placeholder={getFieldTooltip(field)}
                                        value={formState[field] || ''}
                                        onChange={handleInputChange}
                                        style={{ marginBottom: '8px', width: '100%' }}
                                        onFocus={(e) => e.target.nextSibling.classList.add('visible')}
                                        onBlur={(e) => e.target.nextSibling.classList.remove('visible')}
                                        onMouseEnter={(e) => e.target.nextSibling.classList.add('visible')}
                                        onMouseLeave={(e) => e.target.nextSibling.classList.remove('visible')}
                                    />
                                    <div className="field-tooltip">{getFieldTooltip(field)}</div>
                                </div>
                            ) : (
                                <div style={{ marginBottom: '4px' }}>
                                    {addr[field] || ''}
                                </div>
                            )}
                        </div>
                    ))}
            </div>
            <div className="address-actions">
                {isEditing ? (
                    <>
                        <button
                            className="address-action-btn address-btn-edit"
                            disabled={!validateForm()}
                            style={{ opacity: validateForm() ? 1 : 0.6 }}
                            onClick={() => {
                                handleAddressSave(isNew, dispatch, formState, addr);
                            }}
                        >
                            Save
                        </button>
                        <button
                            className="address-action-btn address-btn-delete"
                            onClick={() => {
                                setEditingId(null);
                                setIsNewAddress(false);
                            }}
                        >
                            Cancel
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            className="address-action-btn address-btn-edit"
                            onClick={() => handleEditClick(addr)}
                        >
                            Edit
                        </button>
                        <button
                            className="address-action-btn address-btn-delete"
                            onClick={() => handleDeleteAddress(addr.id || addr._id)}
                        >
                            Delete
                        </button>
                        {!addr.isDefault && (
                            <button
                                className="address-action-btn address-btn-default"
                                onClick={() => handleSetDefault(addr.id || addr._id)}
                            >
                                Set Default
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    function handleAddressSave(isNew, dispatch, formState, addr) {
        if (isNew) {
            dispatch(addAddress({ userId: userId, address: formState }));
        } else {
            dispatch(editAddress({ userId: userId, address: formState }));
        }
        setEditingId(null);
        setIsNewAddress(false);
    }

    return (
        <div className="addresses">
            <main className="addresses-content">
                <section className="addresses-section">
                    <div className="address-section-header">
                        <div>
                            <h2 className="address-section-title">Saved Addresses</h2>
                            <p className="address-section-subtitle">Manage your shipping and billing addresses</p>
                        </div>
                        <button
                            className="add-address-btn"
                            onClick={handleAddAddress}
                            disabled={isDeleting || editingId}
                            style={{ opacity: isDeleting || editingId ? 0.6 : 1 }}
                        >
                            + Add New Address
                        </button>

                    </div>
                    <div className="addresses-grid">
                        {/* New address card placeholder */}
                        {editingId === 'new' && renderAddressCard(formState, true, true)}
                        {/* Existing addresses */}
                        {addresses.map(addr => {
                            const isEditing = editingId === addr.id;
                            return renderAddressCard(addr, isEditing);
                        })}
                    </div>

                </section>
            </main>
        </div>
    );
};

export default Addresses;

